/**
 * System Health Dashboard Constants
 */

import { I18n } from 'react-redux-i18n';
import { DependencyDisplayInfo, EventDisplayInfo, DependencyKey, EventKey } from './types';

// i18n key prefix
const I18N_PREFIX = 'systemSettings.systemHealth';

// Refresh interval in milliseconds (2 minutes)
export const REFRESH_INTERVAL_MS = 2 * 60 * 1000;

// Maximum gap between readings to consider them continuous (3 minutes)
export const MAX_HISTORY_GAP_MS = 3 * 60 * 1000;

// Maximum number of history entries to store
export const MAX_HISTORY_ENTRIES = 30;

// Session storage key for latency history
export const LATENCY_HISTORY_KEY = 'systemHealth.latencyHistory';

// Dependency latency thresholds (in ms)
export const DEPENDENCY_THRESHOLDS: Record<DependencyKey, { healthy: number; degraded: number }> = {
  database: { healthy: 50, degraded: 500 },
  cache: { healthy: 50, degraded: 500 },
  blobStorage: { healthy: 50, degraded: 500 },
  serviceBus: { healthy: 200, degraded: 750 }
};

// Event time thresholds (in milliseconds)
export const EVENT_THRESHOLDS: Record<EventKey, { healthy: number; degraded: number }> = {
  lastPublicDonation: {
    healthy: 30 * 60 * 1000,      // 30 minutes
    degraded: 60 * 60 * 1000      // 1 hour
  },
  lastActiveEnduranceDonation: {
    healthy: 3 * 24 * 60 * 60 * 1000,   // 3 days
    degraded: 7 * 24 * 60 * 60 * 1000   // 7 days
  },
  lastSale: {
    healthy: 6 * 60 * 60 * 1000,        // 6 hours
    degraded: 24 * 60 * 60 * 1000       // 24 hours
  },
  lastRecurringDonation: {
    healthy: 7 * 24 * 60 * 60 * 1000,   // 7 days
    degraded: 14 * 24 * 60 * 60 * 1000  // 14 days
  },
  lastBankAccountDonation: {
    healthy: 7 * 24 * 60 * 60 * 1000,   // 7 days
    degraded: 14 * 24 * 60 * 60 * 1000  // 14 days
  },
  lastRaceRosterFundraiser: {
    healthy: 24 * 60 * 60 * 1000,       // 1 day
    degraded: 3 * 24 * 60 * 60 * 1000   // 3 days
  },
  lastActiveEnduranceFundraiser: {
    healthy: 24 * 60 * 60 * 1000,       // 1 day
    degraded: 3 * 24 * 60 * 60 * 1000   // 3 days
  },
  lastNjukoFundraiser: {
    healthy: 24 * 60 * 60 * 1000,       // 1 day
    degraded: 3 * 24 * 60 * 60 * 1000   // 3 days
  },
  lastOnboardingOrganization: {
    healthy: 3 * 24 * 60 * 60 * 1000,   // 3 days
    degraded: 7 * 24 * 60 * 60 * 1000   // 7 days
  },
  lastOnboardingOrgPaymentPlatform: {
    healthy: 7 * 24 * 60 * 60 * 1000,   // 7 days
    degraded: 14 * 24 * 60 * 60 * 1000  // 14 days
  },
  lastEventAmbassador: {
    healthy: 7 * 24 * 60 * 60 * 1000,   // 7 days
    degraded: 14 * 24 * 60 * 60 * 1000  // 14 days
  },
  lastDonationPlanEventAmbassador: {
    healthy: 14 * 24 * 60 * 60 * 1000,  // 14 days
    degraded: 30 * 24 * 60 * 60 * 1000  // 30 days
  }
};

// Dependency display configuration (labels are i18n keys)
export const DEPENDENCY_INFO: DependencyDisplayInfo[] = [
  { key: 'database', label: `${I18N_PREFIX}.dependencies.database`, iconName: 'Database01' },
  { key: 'cache', label: `${I18N_PREFIX}.dependencies.cache`, iconName: 'Server01' },
  { key: 'blobStorage', label: `${I18N_PREFIX}.dependencies.blobStorage`, iconName: 'HardDrive' },
  { key: 'serviceBus', label: `${I18N_PREFIX}.dependencies.serviceBus`, iconName: 'Dataflow02' }
];

// Event display configuration (labels are i18n keys)
export const EVENT_INFO: EventDisplayInfo[] = [
  { key: 'lastPublicDonation', label: `${I18N_PREFIX}.events.lastPublicDonation` },
  { key: 'lastActiveEnduranceDonation', label: `${I18N_PREFIX}.events.lastActiveEnduranceDonation` },
  { key: 'lastSale', label: `${I18N_PREFIX}.events.lastSale` },
  { key: 'lastRecurringDonation', label: `${I18N_PREFIX}.events.lastRecurringDonation` },
  { key: 'lastBankAccountDonation', label: `${I18N_PREFIX}.events.lastBankAccountDonation` },
  { key: 'lastRaceRosterFundraiser', label: `${I18N_PREFIX}.events.lastRaceRosterFundraiser` },
  { key: 'lastActiveEnduranceFundraiser', label: `${I18N_PREFIX}.events.lastActiveEnduranceFundraiser` },
  { key: 'lastNjukoFundraiser', label: `${I18N_PREFIX}.events.lastNjukoFundraiser` },
  { key: 'lastOnboardingOrganization', label: `${I18N_PREFIX}.events.lastOnboardingOrganization` },
  { key: 'lastOnboardingOrgPaymentPlatform', label: `${I18N_PREFIX}.events.lastOnboardingOrgPaymentPlatform` },
  { key: 'lastEventAmbassador', label: `${I18N_PREFIX}.events.lastEventAmbassador` },
  { key: 'lastDonationPlanEventAmbassador', label: `${I18N_PREFIX}.events.lastDonationPlanEventAmbassador` }
];

// Helper to get status labels
export const getStatusLabel = (status: string): string => {
  return I18n.t(`${I18N_PREFIX}.status.${status}`);
};

// Helper to get event status labels (softer language)
export const getEventStatusLabel = (status: string): string => {
  return I18n.t(`${I18N_PREFIX}.eventStatus.${status}`);
};
